import { motion } from 'framer-motion';
import { Compass, Cloud, Network, Shield } from 'lucide-react';
import { BrandDot } from '../components/brand/BrandMotif';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const proficiencies = [
  {
    icon: Compass,
    title: 'Strategic Advisory',
    description: 'SGA provides strategic guidance to organizations navigating complex business transformations. Our advisory approach aligns corporate strategy with execution frameworks that ensure measurable outcomes.',
    items: [
      'Business strategy formulation',
      'Market positioning and revenue architecture',
      'Transformation roadmaps',
      'Governance frameworks',
    ],
  },
  {
    icon: Cloud,
    title: 'Digital Transformation',
    description: 'Digital transformation is not merely technology adoption but a shift in operating models. SGA supports organizations in redesigning digital architectures that improve efficiency, scalability, and customer experience.',
    items: [
      'Cloud adoption strategy',
      'Digital infrastructure design',
      'Data center and network modernization',
      'Cybersecurity architecture',
    ],
  },
  {
    icon: Network,
    title: 'Enterprise Architecture & Integration',
    description: 'Modern enterprises depend on integrated systems and platforms. SGA enables seamless integration across applications, infrastructure, and vendor ecosystems.',
    items: [
      'System integration',
      'Technology ecosystem alignment',
      'Unified communications infrastructure',
      'Enterprise platform integration',
    ],
  },
  {
    icon: Shield,
    title: 'Governance and Compliance',
    description: 'SGA supports organizations in establishing governance structures that ensure operational integrity and regulatory compliance.',
    items: [
      'Transformation governance frameworks',
      'Regulatory advisory',
      'Contract and vendor governance',
      'Risk management oversight',
    ],
  },
];

export const ProficiencyPage = () => {
  return (
    <div data-testid="proficiency-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3)), url('/assets/banners/Proficiency_banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-lg"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-4"
            >
              Where Strategy Meets Execution
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-inter text-base text-white leading-relaxed"
            >
              Deep expertise across enterprise strategy, digital infrastructure, 
              and transformation governance.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Our Expertise
            </span>
            <h2 className="heading-section text-[#0B1E2D] mb-6">
              Strategic and Technology Proficiency
            </h2>
            <p className="body-text">
              SGA STRAT combines strategic thinking with deep operational and technological 
              expertise to deliver transformation programs that create sustainable business value.
            </p>
          </div>
        </div>
      </section>

      {/* Proficiency Grid */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {proficiencies.map((proficiency, index) => (
              <motion.div
                key={proficiency.title}
                data-testid={`proficiency-card-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-white border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500 p-8 flex flex-col hover:border-[#0087B4]/30"
              >
                <div className="flex items-start gap-6 mb-6">
                  <div className="w-14 h-14 flex-shrink-0 bg-[#4DB848]/10 flex items-center justify-center">
                    <proficiency.icon className="w-7 h-7 text-[#4DB848]" />
                  </div>
                  <div>
                    <h3 className="font-montserrat font-bold text-xl text-[#0B1E2D] mb-2">
                      {proficiency.title}
                    </h3>
                  </div>
                </div>
                
                <p className="font-inter text-slate-600 leading-relaxed mb-6">
                  {proficiency.description}
                </p>
                
                <ul className="space-y-3 mt-auto">
                  {proficiency.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <BrandDot size="xs" />
                      <span className="font-inter text-sm text-slate-500">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Brand motif hover effect */}
                <div className="h-2 flex justify-center items-center gap-1 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {[1.5, 2, 2.5, 2, 1.5].map((size, i) => (
                    <span 
                      key={i}
                      className="rounded-full bg-[#4DB848]"
                      style={{ width: size * 2.5, height: size * 2.5 }}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0B1E2D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="heading-section text-white mb-6">
            Leverage Our Expertise
          </h2>
          <p className="font-inter text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Discover how SGA STRAT can help your organization achieve strategic 
            and operational excellence.
          </p>
        </div>
      </section>
    </div>
  );
};
